import { Card, Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { MAX_PAGE_SIZE, PaginationInput } from "../../repository/data.default";
import {
  QueryCondition,
  QueryOperation,
} from "../../code/search-query/search-query";
import { bucketByDay, groupByField } from "./AnalyticsRange";
import {
  ChartKind,
  MetricChartEmpty,
  renderMetricChart,
} from "./MetricChartRender";

// Platform-wide (all-companies) metric card: fetches the population once and reduces it client-side into a
// trend line or categorical breakdown, reusing AnalyticsRange + the shared MetricChart renderer.
//
// SCALE CAVEAT: this reduces client-side and is capped at MAX_PAGE_SIZE (1000) rows. To keep platform-wide
// cards honest, pass `windowDays` (+ a `dateField`) so the fetch is bounded to a recent window rather than
// all-time. The real fix for unbounded platform rollups is a server-side aggregate endpoint (deferred).
type Mode =
  | { kind: "trend"; dateField: string; valueLabel?: string }
  | {
      kind: "breakdown";
      groupField: string;
      chart: "pie" | "ring" | "column";
      dateField?: string;
      valueLabel?: string;
    };

export interface DashboardMetricCardProps {
  readonly title: string;
  readonly fetch: (
    input: PaginationInput,
  ) => Promise<{ data: any[]; totalRecords: number }>;
  readonly mode: Mode;
  /** Bound the fetch to the last N days (applied on the mode's dateField). Default 180. */
  readonly windowDays?: number;
  /** Shared scope conditions injected by a section filter (e.g. company/department equals). */
  readonly scope?: QueryCondition[];
  /** Explicit date bounds [start, end] from a section filter — overrides the default windowDays window. */
  readonly range?: [string, string];
}

function DashboardMetricCard(props: DashboardMetricCardProps) {
  const { mode } = props;
  const windowDays = props.windowDays ?? 180;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [trendData, setTrendData] = useState<{ [day: string]: number }>({});
  const [groupData, setGroupData] = useState<{ type: string; value: number }[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    const dateField = mode.dateField;
    // A section-supplied range overrides the card's default window; both go on the card's own dateField.
    const dateCondition: QueryCondition[] =
      props.range && dateField
        ? [{ field: dateField, operation: QueryOperation.Between, values: [props.range[0], props.range[1]] }]
        : dateField
          ? [
              {
                field: dateField,
                operation: QueryOperation.Between,
                values: [
                  dayjs().subtract(windowDays, "day").format("YYYY-MM-DD"),
                  dayjs().add(1, "day").format("YYYY-MM-DD"),
                ],
              },
            ]
          : [];

    const conditions = [...(props.scope ?? []), ...dateCondition];

    props
      .fetch({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return;
        if (mode.kind === "trend") {
          setTrendData(bucketByDay(res.data, mode.dateField));
        } else {
          setGroupData(groupByField(res.data, mode.groupField));
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // Refetch when the section filter (scope/range) or window changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.scope), props.range?.[0], props.range?.[1], windowDays]);

  const valueLabel = mode.valueLabel ?? "Count";
  const kind: ChartKind = mode.kind === "trend" ? "line" : mode.chart;

  return (
    <Card title={props.title} className="flex-auto">
      <Spin spinning={loading}>
        {error ? (
          <MetricChartEmpty label="Something went wrong" />
        ) : (
          renderMetricChart(kind, trendData, groupData, valueLabel, 260)
        )}
      </Spin>
    </Card>
  );
}

export default DashboardMetricCard;
